import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Paper,
  Button
} from '@mui/material';
import {
  QrCode2,
  School,
  CheckCircle,
  EventSeat
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Modern Student Dashboard with animations and better UI
 */
function DashboardPage() {
  const { user } = useContext(AuthContext);

  // Redirect based on role
  if (user?.role === 'librarian') {
    return <Navigate to="/librarian" replace />;
  }
  if (user?.role === 'admin') {
    return <Navigate to="/admin-dashboard" replace />;
  }
  if (user?.role === 'institute_manager') {
    return <Navigate to="/institute-manager-dashboard" replace />;
  }
  if (user?.role === 'marketing') {
    return <Navigate to="/marketing-dashboard" replace />;
  }
  if (user?.role === 'student') {
    return <Navigate to="/student" replace />;
  }
  if (user?.role === 'sales') {
    return <Navigate to="/sales-dashboard" replace />;
  }
  if (user?.role === 'seo') {
    return <Navigate to="/seo-dashboard" replace />;
  }

  const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
  const qrUrl = user?.qr_url ? `${API_BASE}${user.qr_url}` : null;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'transparent',
      pt: 4,
      pb: 8
    }}>
      <Container maxWidth="lg">
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Welcome Header */}
          <MotionBox variants={itemVariants} mb={4}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'background.paper',
                backdropFilter: 'blur(10px)',
                borderRadius: 4
              }}
            >
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: 'primary.main',
                    fontSize: '2rem'
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box flex={1} minWidth="200px">
                  <Typography variant="h4" fontWeight="700" gutterBottom>
                    Welcome back, {user?.name}!
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                    <Chip
                      icon={<School />}
                      label={user?.role?.toUpperCase()}
                      color="primary"
                      size="small"
                    />
                    {user?.city && (
                      <Chip
                        label={user.city}
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {user?.age && (
                      <Chip
                        label={`Age: ${user.age}`}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Paper>
          </MotionBox>

          <Grid container spacing={3}>
            {/* QR Code Card */}
            <Grid item xs={12} md={6}>
              <MotionCard
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                sx={{
                  height: '100%',
                  background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <QrCode2 sx={{ fontSize: 40 }} />
                    <Box>
                      <Typography variant="h5" fontWeight="600">
                        Your Digital Pass
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Show this at the library entrance
                      </Typography>
                    </Box>
                  </Box>

                  {qrUrl ? (
                    <Box
                      textAlign="center"
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 3,
                        p: 2,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <MotionBox
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        <img
                          src={qrUrl}
                          alt="Your QR Code"
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: '12px'
                          }}
                        />
                        <Typography
                          variant="caption"
                          display="block"
                          mt={1}
                          color="text.secondary"
                          fontWeight="500"
                        >
                          ID: {user?.static_qr_id}
                        </Typography>
                      </MotionBox>
                    </Box>
                  ) : (
                    <Box
                      textAlign="center"
                      py={4}
                      bgcolor="rgba(255,255,255,0.1)"
                      borderRadius={2}
                    >
                      <Typography>QR code not available</Typography>
                    </Box>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      bgcolor: 'white',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)'
                      }
                    }}
                    startIcon={<QrCode2 />}
                  >
                    Download QR Code
                  </Button>
                </CardContent>
              </MotionCard>
            </Grid>

            {/* Stats and Info */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {/* Profile Info Card */}
                <Grid item xs={12}>
                  <MotionCard
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    sx={{ bgcolor: 'background.paper' }}
                  >
                    <CardContent>
                      <Typography variant="h6" fontWeight="600" gutterBottom>
                        Profile Information
                      </Typography>
                      <Box mt={2} display="flex" flexDirection="column" gap={1.5}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="text.secondary">Email</Typography>
                          <Typography fontWeight="500">{user?.email}</Typography>
                        </Box>
                        {user?.phone && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography color="text.secondary">Phone</Typography>
                            <Typography fontWeight="500">{user.phone}</Typography>
                          </Box>
                        )}
                        {user?.age && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography color="text.secondary">Age</Typography>
                            <Typography fontWeight="500">{user.age}</Typography>
                          </Box>
                        )}
                        {user?.city && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography color="text.secondary">City</Typography>
                            <Typography fontWeight="500">{user.city}</Typography>
                          </Box>
                        )}
                        {user?.interested_courses && (
                          <Box display="flex" justifyContent="space-between">
                            <Typography color="text.secondary">Interested Courses</Typography>
                            <Typography fontWeight="500" textAlign="right" sx={{ maxWidth: '60%' }}>
                              {user.interested_courses}
                            </Typography>
                          </Box>
                        )}
                        <Box display="flex" justifyContent="space-between">
                          <Typography color="text.secondary">Member Since</Typography>
                          <Typography fontWeight="500">
                            {new Date(user?.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </MotionCard>
                </Grid>

                {/* Quick Stats */}
                <Grid item xs={6}>
                  <MotionCard
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    sx={{
                      background: 'linear-gradient(135deg, #F7B32B 0%, #C88A00 100%)',
                      color: 'white'
                    }}
                  >
                    <CardContent>
                      <EventSeat sx={{ fontSize: 32, mb: 1 }} />
                      <Typography variant="h4" fontWeight="700">0</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Active Bookings
                      </Typography>
                    </CardContent>
                  </MotionCard>
                </Grid>

                <Grid item xs={6}>
                  <MotionCard
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    sx={{
                      background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                      color: 'white'
                    }}
                  >
                    <CardContent>
                      <CheckCircle sx={{ fontSize: 32, mb: 1 }} />
                      <Typography variant="h4" fontWeight="700">0</Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Completed
                      </Typography>
                    </CardContent>
                  </MotionCard>
                </Grid>
              </Grid>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12}>
              <MotionCard variants={itemVariants} sx={{ bgcolor: 'background.paper' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" fontWeight="600" gutterBottom>
                    Quick Actions
                  </Typography>
                  <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        sx={{ py: 2 }}
                        href="/libraries"
                      >
                        Browse Libraries
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        sx={{ py: 2 }}
                        href="/student/access"
                      >
                        My Bookings
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        sx={{ py: 2 }}
                        href="/profile"
                      >
                        View Profile
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="large"
                        sx={{ py: 2 }}
                      >
                        Get Support
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default DashboardPage;
